import $http from '@vue-util/http';

import { errorHandler } from '@/utils/ErrorHandler';
import { INSTORE_ORDER_HISTORY_ENDPOINT } from '#shared/endpoints';
import { ORDER_HISTORY } from 'config';

import { useOrderHistoryStore } from '@/stores/ppe/useOrderHistoryStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';

const {
  inStoreHistory: { inStoreFallbackError },
} = ORDER_HISTORY;

function transformShipmentItems(shipment = [{ items: [] }]) {
  const items = shipment[0]?.items;
  return items.map((item) => {
    const transformedItem = item;
    transformedItem.product = item?.product || {};
    transformedItem.product.imagery = item?.product?.imagery || {};
    transformedItem.product.detail = item?.product?.detail || {};
    transformedItem.product.price = item?.product?.price || [];
    transformedItem.product.traits = item?.product?.traits || {};
    transformedItem.urlTemplate = item?.product?.imagery?.urlTemplate;
    transformedItem.primaryImageFilePath = item?.product?.imagery?.primaryImage?.filePath;
    return transformedItem;
  });
}

export const useInstoreOrderHistoryStore = defineStore('instoreOrderHistoryStore', {
  state: () => ({
    shipments: [],
    selectedShipment: null,
    nextOffset: 0,
    error: null,
    isLoading: false,
    noCardInWallet: false,
  }),
  actions: {
    SET_INSTORE_ORDER_HISTORY({ shipments = [], nextOffset = 0, overwrite = false, noCardInWallet = false }) {
      if (overwrite) {
        this.shipments = shipments;
      } else {
        this.shipments = [...this.shipments, ...shipments];
      }
      this.nextOffset = nextOffset;
      this.error = null;
      this.noCardInWallet = noCardInWallet;
    },
    SET_INSTORE_ORDER_HISTORY_ERROR(error) {
      this.error = error;
    },
    SET_INSTORE_ORDER_HISTORY_LOADER(isLoading) {
      this.isLoading = isLoading;
    },

    async GET_INSTORE_ORDER_HISTORY({ dateRange, userToken, dateFilterUpdated = false }) {
      if (this.isLoading) return; // prevent API call while already waiting for previous response
      await this.SET_INSTORE_ORDER_HISTORY_ERROR(null);
      if (dateFilterUpdated) await this.SET_INSTORE_ORDER_HISTORY({ overwrite: true });
      await this.SET_INSTORE_ORDER_HISTORY_LOADER(true);
      const envPropsStore = useEnvPropsStore();
      try {
        const response = await $http.get(INSTORE_ORDER_HISTORY_ENDPOINT, {
          headers: {
            'X-Macys-UID': encodeURIComponent(userToken),
            'X-Macys-ClientId': 'SITE',
            'X-Macys-DeviceId': envPropsStore.envProps.deviceType,
          },
          params: {
            type: 'store',
            offset: this.nextOffset,
            limit: 10,
            ...(dateRange && { year: dateRange }),
          },
        });
        if (!response.data?.orders?.order) {
          throw new Error();
        }
        const result = response?.data?.orders?.order;
        const instoreStatusMap = {
          retrn: 'Returned',
          purchase: 'Purchased',
          exchange: 'Exchange',
        };
        const shipments = result.map((shipment) => {
          const purchasedItems = transformShipmentItems(shipment?.shipments);
          const returnedItems = transformShipmentItems(shipment?.returnedShipments);
          return {
            shipmentStatusCode: 401,
            shipmentStatus: instoreStatusMap[shipment?.transactionHeader?.dcrTransCode] || '',
            shipmentTotal: shipment?.total,
            storeName: shipment?.transactionHeader?.store?.details?.name,
            shipmentConfirmationDate: shipment?.confirmationDate,
            receiptIdentifier: shipment?.transactionHeader?.receiptIdentifier,
            receiptTimestamp: shipment?.transactionHeader?.receiptTimestamp,
            ...(shipment?.transactionHeader?.dcrTransCode === 'exchange' && {
              purchasedItems,
              returnedItems,
            }),
            items: [
              ...(purchasedItems[0] ? [purchasedItems[0]] : []),
              ...(returnedItems[0] ? [returnedItems[0]] : []),
              ...purchasedItems.slice(1),
              ...returnedItems.slice(1),
            ],
          };
        });
        await this.SET_INSTORE_ORDER_HISTORY({
          shipments,
          nextOffset: response?.data?.orders?.meta?.nextOffset,
          overwrite: dateFilterUpdated,
          noCardInWallet: response?.data?.orders?.noCardInWallet,
        });
        await useOrderHistoryStore().SET_KILLSWITCH(response?.data?.killSwitch || { disableApisWithUserIdInPayload: true });
      } catch (error) {
        await this.SET_INSTORE_ORDER_HISTORY_ERROR({
          errorCode: error?.response?.data?.errors?.error?.[0].errorCode,
          message: errorHandler(error, inStoreFallbackError),
        });
      } finally {
        await this.SET_INSTORE_ORDER_HISTORY_LOADER(false);
      }
    },

    SELECTED_SHIPMENT(shipment) {
      this.selectedShipment = shipment;
    },
    CLEAR_SELECTED_SHIPMENT() {
      this.selectedShipment = null;
    },
    CLEAR_INSTORE_ORDER_HISTORY() {
      this.SET_INSTORE_ORDER_HISTORY({ overwrite: true });
    },
  },
});
